import { useState, useEffect } from 'react';
import api from './api';

// ─── Brand colours ────────────────────────────────────────────────────────────
const RED   = '#e63946';
const DARK  = '#0d0d1e';
const CARD  = 'rgba(10, 10, 10, 0.80)';
const GRAY  = '#9ca3af';
const LIGHT = '#f3f4f6';

export default function Login({ onLogin }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  // Якщо відкрито лінк з листа /reset-password?token=... — одразу сторінка скидання
  const [resetToken] = useState(() => {
    if (!window.location.pathname.includes('reset-password')) return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  });
  // Той самий патерн для /accept-invite?token=... (лист "You're invited")
  const [inviteToken] = useState(() => {
    if (!window.location.pathname.includes('accept-invite')) return '';
    return new URLSearchParams(window.location.search).get('token') || '';
  });
  const [page,     setPage]     = useState(resetToken ? 'reset' : inviteToken ? 'accept-invite' : 'login'); // 'login' | 'register' | 'forgot' | 'reset' | 'accept-invite'

  // ── Login ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.auth.login(email.trim(), password);
      api.setToken(data.token);
      onLogin(data.user, data.token);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Shared input style ─────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    transition: 'border-color 0.2s', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.07)', color: '#fff',
  };

  const backToLogin = () => {
    window.history.replaceState(null, '', '/'); // прибрати token з адресного рядка
    setPage('login');
  };

  if (page === 'register')       return <RegisterPage      onBack={() => setPage('login')} />;
  if (page === 'forgot')         return <ForgotPage        onBack={() => setPage('login')} />;
  if (page === 'reset')          return <ResetPage         token={resetToken}  onBack={backToLogin} />;
  if (page === 'accept-invite')  return <AcceptInvitePage  token={inviteToken} onBack={backToLogin} onLogin={onLogin} />;

  // ─────────────────────────────────────────────────────────────────────────
  // LOGIN FORM
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'url(https://scullcrew.net/wp-content/uploads/2018/06/44341411.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} />
      <div style={{
        background: CARD, borderRadius: '20px', padding: '44px 40px',
        width: '100%', maxWidth: '420px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', zIndex: 1,
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img
            src="/scull-logo.png"
            alt="Scull Crew"
            style={{ width: '250px', height: 'auto', marginBottom: '16px' }}
          />
          <h1 style={{
            margin: 0, fontSize: '20px', fontWeight: 400, color: '#fff',
            fontFamily: 'Georgia, "Times New Roman", serif',
            letterSpacing: '0.18em', textTransform: 'uppercase',
            WebkitTextStroke: '1px rgba(255,255,255,0.8)',
          }}>
            Crew Portal
          </h1>
        </div>

        <h2 style={{ margin: '0 0 24px', fontSize: '17px', fontWeight: 600, color: LIGHT }}>
          Sign in to your account
        </h2>

        {error && (
          <div style={{
            background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)',
            borderRadius: '8px', padding: '10px 14px', color: '#f87171',
            fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@scullcrew.net"
              required
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => setPage('forgot')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontSize: '13px', padding: 0 }}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? '#555' : RED,
              color: '#fff', border: 'none', borderRadius: '8px',
              padding: '13px', fontSize: '14px', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: GRAY }}>
          Don't have an account?{' '}
          <button
            onClick={() => setPage('register')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: RED, fontWeight: 600, padding: 0 }}
          >
            Register
          </button>
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REGISTER PAGE
// ─────────────────────────────────────────────────────────────────────────────
function RegisterPage({ onBack }) {
  const [form, setForm] = useState({
    givenName: '', surname: '', email: '', password: '', confirm: '',
    country: '', phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      await api.auth.register({
        givenName: form.givenName, surname: form.surname, email: form.email, password: form.password,
        country: form.country, phone: form.phone,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: '#fff',
  };
  const label = (text) => (
    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '5px' }}>
      {text}
    </label>
  );

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundImage: 'url(https://scullcrew.net/wp-content/uploads/2018/06/44341411.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} />
      <div style={{
        background: CARD, borderRadius: '20px', padding: '40px',
        width: '100%', maxWidth: '480px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', zIndex: 1,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GRAY, fontSize: '13px', marginBottom: '16px', padding: 0 }}>
          ← Back to sign in
        </button>
        <h2 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>Create an account</h2>

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <p style={{ fontWeight: 600, color: '#fff', marginBottom: '8px' }}>Registration submitted!</p>
            <p style={{ color: GRAY, fontSize: '14px' }}>Your account is pending approval by the administrator.</p>
            <button onClick={onBack} style={{ marginTop: '20px', background: RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600 }}>
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>
                {error}
              </div>
            )}
            <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  {label('Given name *')}
                  <input style={inputStyle} value={form.givenName} onChange={e => set('givenName', e.target.value)} placeholder="Ivan" required />
                </div>
                <div>
                  {label('Surname *')}
                  <input style={inputStyle} value={form.surname} onChange={e => set('surname', e.target.value)} placeholder="Kovalenko" required />
                </div>
              </div>
              <div>
                {label('Email address *')}
                <input type="email" style={inputStyle} value={form.email} onChange={e => set('email', e.target.value)} placeholder="ivan@example.com" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  {label('Password *')}
                  <input type="password" style={inputStyle} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 8 chars" required minLength={8} />
                </div>
                <div>
                  {label('Confirm password *')}
                  <input type="password" style={inputStyle} value={form.confirm} onChange={e => set('confirm', e.target.value)} placeholder="Repeat" required />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  {label('Country')}
                  <input style={inputStyle} value={form.country} onChange={e => set('country', e.target.value)} placeholder="Ukraine" />
                </div>
                <div>
                  {label('Phone')}
                  <input style={inputStyle} value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+380..." />
                </div>
              </div>
              <button
                type="submit" disabled={loading}
                style={{ background: loading ? '#555' : RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', marginTop: '4px' }}
              >
                {loading ? 'Registering…' : 'Create account'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORGOT PASSWORD PAGE
// ─────────────────────────────────────────────────────────────────────────────
// ─── Reset password (з лінка у листі) ────────────────────────────────────────

function ResetPage({ token, onBack }) {
  const [pass1,   setPass1]   = useState('');
  const [pass2,   setPass2]   = useState('');
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (pass1.length < 8)  { setError('Password must be at least 8 characters'); return; }
    if (pass1 !== pass2)   { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.auth.resetPassword(token, pass1);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Invalid or expired reset link');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundImage: 'url(https://scullcrew.net/wp-content/uploads/2018/06/44341411.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} />
      <div style={{
        background: CARD, borderRadius: '20px', padding: '40px',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', zIndex: 1,
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>Set new password</h2>

        {done ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>✅</div>
            <p style={{ fontWeight: 600, color: '#fff' }}>Password changed</p>
            <p style={{ color: GRAY, fontSize: '13px' }}>You can now sign in with your new password.</p>
            <button onClick={onBack} style={{ marginTop: '20px', background: RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600 }}>
              Sign in
            </button>
          </div>
        ) : (
          <>
            <p style={{ color: GRAY, fontSize: '13px', marginBottom: '24px' }}>
              Enter a new password for your account (min. 8 characters).
            </p>
            {error && (
              <div style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '13px', marginBottom: '14px' }}>
                {error}
              </div>
            )}
            <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>New password</label>
                <input type="password" value={pass1} onChange={e => setPass1(e.target.value)} required style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>Confirm password</label>
                <input type="password" value={pass2} onChange={e => setPass2(e.target.value)} required style={inputStyle} />
              </div>
              <button
                type="submit" disabled={loading}
                style={{ background: loading ? '#555' : RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Saving…' : 'Change password'}
              </button>
            </form>
            <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GRAY, fontSize: '13px', marginTop: '16px', padding: 0 }}>
              ← Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCEPT INVITE PAGE (з лінка "You're invited" — картка вже створена адміном,
// тут лише встановлюється пароль; акаунт стає active одразу, без Approve)
// ─────────────────────────────────────────────────────────────────────────────
function AcceptInvitePage({ token, onBack, onLogin }) {
  const [checking, setChecking] = useState(true);
  const [invite,   setInvite]   = useState(null); // { firstName, lastName, email }
  const [checkError, setCheckError] = useState('');

  const [pass1,   setPass1]   = useState('');
  const [pass2,   setPass2]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!token) { setCheckError('Missing invitation token'); setChecking(false); return; }
    api.auth.verifyInvite(token)
      .then(res => setInvite(res))
      .catch(err => setCheckError(err.message || 'Invalid or expired invitation link'))
      .finally(() => setChecking(false));
  }, [token]);

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (pass1.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (pass1 !== pass2)  { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const data = await api.auth.acceptInvite(token, pass1);
      api.setToken(data.token);
      onLogin(data.user, data.token);
    } catch (err) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundImage: 'url(https://scullcrew.net/wp-content/uploads/2018/06/44341411.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} />
      <div style={{
        background: CARD, borderRadius: '20px', padding: '40px',
        width: '100%', maxWidth: '420px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', zIndex: 1,
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>You're invited</h2>

        {checking ? (
          <p style={{ color: GRAY, fontSize: '13px' }}>Checking your invitation…</p>
        ) : checkError ? (
          <>
            <div style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>
              {checkError}
            </div>
            <p style={{ color: GRAY, fontSize: '13px' }}>Ask the administrator to send a new invitation.</p>
            <button onClick={onBack} style={{ marginTop: '16px', background: 'none', border: 'none', cursor: 'pointer', color: GRAY, fontSize: '13px', padding: 0 }}>
              ← Back to sign in
            </button>
          </>
        ) : (
          <>
            <p style={{ color: GRAY, fontSize: '13px', marginBottom: '24px' }}>
              Welcome, {[invite.firstName, invite.lastName].filter(Boolean).join(' ') || 'there'}. Set a password for
              {' '}<b style={{ color: LIGHT }}>{invite.email}</b> to activate your Scull Crew Portal account.
            </p>
            {error && (
              <div style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '13px', marginBottom: '14px' }}>
                {error}
              </div>
            )}
            <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>Password</label>
                <input type="password" value={pass1} onChange={e => setPass1(e.target.value)} placeholder="Min. 8 chars" required minLength={8} style={inputStyle} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>Confirm password</label>
                <input type="password" value={pass2} onChange={e => setPass2(e.target.value)} placeholder="Repeat" required style={inputStyle} />
              </div>
              <button
                type="submit" disabled={loading}
                style={{ background: loading ? '#555' : RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                {loading ? 'Activating…' : 'Activate account'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ForgotPage({ onBack }) {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', color: '#fff',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundImage: 'url(https://scullcrew.net/wp-content/uploads/2018/06/44341411.jpg)',
      backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} />
      <div style={{
        background: CARD, borderRadius: '20px', padding: '40px',
        width: '100%', maxWidth: '400px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.08)',
        position: 'relative', zIndex: 1,
      }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GRAY, fontSize: '13px', marginBottom: '16px', padding: 0 }}>
          ← Back to sign in
        </button>
        <h2 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#fff' }}>Forgot password</h2>
        <p style={{ color: GRAY, fontSize: '13px', marginBottom: '24px' }}>
          Enter your email and we'll send a reset link.
        </p>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>📧</div>
            <p style={{ fontWeight: 600, color: '#fff' }}>Check your inbox</p>
            <p style={{ color: GRAY, fontSize: '13px' }}>If your email is registered, a reset link has been sent.</p>
            <button onClick={onBack} style={{ marginTop: '20px', background: RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600 }}>
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', borderRadius: '8px', padding: '10px 14px', color: '#f87171', fontSize: '13px', marginBottom: '14px' }}>
                {error}
              </div>
            )}
            <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: GRAY, marginBottom: '6px' }}>Email address</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@scullcrew.net" required style={inputStyle}
                />
              </div>
              <button
                type="submit" disabled={loading}
                style={{ background: loading ? '#555' : RED, color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
